<template>
  <div>
    <RichTextRenderer
      :document="document"
      :node-renderers="renderNodes()" />
  </div>
</template>

<script>
import RichTextRenderer from 'contentful-rich-text-vue-renderer'
import {
  INLINES,
  BLOCKS,
  Document
} from '@contentful/rich-text-types'

export default {
  components: {
    RichTextRenderer
  },
  props: {
    document: {
      type: Document,
      required: true
    }
  },
  methods: {
    renderNodes () {
      const nodeContentWithNewlineBr = (node, key) => node.content.map(childNode => {
        if (childNode.nodeType === 'text') {
          const splitValue = childNode.value.split('\n')
          return splitValue.reduce((aggregate, v, i) => [
            ...aggregate,
            {
              ...childNode,
              value: v
            },
            {
              nodeType: 'break',
              key: `${key}-br-${i}`
            }
          ]
          , []).slice(0, -1)
        }

        return childNode })

      return {
        break: (_node, key, h) => h('br', key, {}),
        // Add explicit list renderers
        [BLOCKS.UL_LIST]: (node, key, h, next) => h('ul', {
          key: key,
          style: 'margin-top: 16px; margin-bottom: 16px;'
        }, next(node.content, key, h, next)),
        [BLOCKS.OL_LIST]: (node, key, h, next) => h('ol', {
          key: key,
          style: 'margin-top: 16px; margin-bottom: 16px;'
        }, next(node.content, key, h, next)),
        [BLOCKS.LIST_ITEM]: (node, key, h, next) => {
          // Mark the list item context for children
          // Process each child and track those that are paragraphs
          const enhancedContent = node.content.map(item => {
            if (item.nodeType === BLOCKS.PARAGRAPH) {
              // Clone the paragraph node and mark it as being inside a list item
              return {
                ...item,
                data: { ...item.data, inListItem: true }
              }
            }
            return item
          })

          return h('li', {
            key: key,
            style: 'margin-bottom: 4px;'
          }, next(enhancedContent, key, h, next))
        },
        [BLOCKS.HEADING_2]: (node, key, h, next) => h('h2', {
          key: key,
          style: 'font-size: 32px;'
          // eslint-disable-next-line prefer-spread
        }, next([].concat.apply([], nodeContentWithNewlineBr(node, key)), key, h, next)),
        [BLOCKS.HEADING_3]: (node, key, h, next) => h('h3', {
          key: key,
          style: 'font-size: 24px;'
          // eslint-disable-next-line prefer-spread
        }, next([].concat.apply([], nodeContentWithNewlineBr(node, key)), key, h, next)),
        [BLOCKS.HEADING_4]: (node, key, h, next) => h('h4', {
          key: key,
          style: 'font-size: 20px;'
          // eslint-disable-next-line prefer-spread
        }, next([].concat.apply([], nodeContentWithNewlineBr(node, key)), key, h, next)),
        [BLOCKS.HEADING_6]: (node, key, h, next) => h('h6', {
          key: key,
          class: 'tc-title--s'
          // eslint-disable-next-line prefer-spread
        }, next([].concat.apply([], nodeContentWithNewlineBr(node, key)), key, h, next)),
        [BLOCKS.PARAGRAPH]: (node, key, h, next) => {
          // Check if this paragraph is inside a list item
          const inListItem = node.data && node.data.inListItem

          return h('p', {
            key: key,
            // Only apply paragraph-100 class if not inside a list item
            class: inListItem ? '' : 'paragraph-100',
            // Add margin 0 to paragraphs inside list items
            style: inListItem ? 'margin: 0;' : ''
            // eslint-disable-next-line prefer-spread
          }, next([].concat.apply([], nodeContentWithNewlineBr(node, key)), key, h, next))
        },
        [INLINES.HYPERLINK]: (node, key, h) => {
          const uri = node.data.uri
          const content = node.content[0].value

          if (uri && content) {
            //In order to place Vue component, Link target in contenful should start with vue.${some-component}
            const vueCompId = 'vue.'

            if (uri.startsWith(vueCompId)) {
              const customComponent = uri.replace(vueCompId, '')

              //mounting a custom vue component with props
              return h(customComponent, {
                key: key,
                props: { content: content }
              })
            }
            //else returns simple anchor link
            return h('a', {
              key: key,
              attrs: {
                target: '_blank',
                href: uri
              },
              class: 'rich-text-link'
            }, content)
          }
          return null
        },
        [[BLOCKS.EMBEDDED_ENTRY]]: (node, key, h) => {
          const entryFields = node.data.target.fields

          //const imgHeight = entryFields.height
          const imgWidth = entryFields.width
          const alignment = entryFields.alignment

          const imgStyle = imgWidth ? `height: auto; width: ${imgWidth}px; margin-top: 16px; margin-bottom: 16px;` : ''
          const assetStyle = `text-align: ${alignment}`

          const asset = entryFields.image.fields.file.url
          if (asset) {
            return h('div', {
              style: assetStyle
            }, [h('img', {
              attrs: {
                src: asset,
                alt: 'img'
              },
              style: imgStyle
            })])
          }
          return null
        },
        [BLOCKS.EMBEDDED_ASSET]: (node, key, h) => {
          const imageStyle = 'width: 100%; height: 100%'
          return h('div', {}, [h('img', {
            attrs: {
              src: node.data.target.fields.file.url,
              alt: node.data.target.fields.description || ''
            },
            style: imageStyle
          })])
        },
        [INLINES.ASSET_HYPERLINK]: (node, key, h) => {
          const uri = node.data.target.fields.file.url
          const content = node.content[0].value

          if (uri && content) {
            return h('a', {
              key: key,
              attrs: {
                target: '_blank',
                href: uri
              },
              class: 'rich-text-link'
            }, content)
          }
          return null
        }
      }
    }
  }
}
</script>

<style scoped>
.rich-text-link {
  color: #6d02a3;
  text-decoration: underline;
}
.rich-text-link:hover {
  color: #4e0174;
  background-color: #e4b6fb;
}
.rich-text-link:active {
  color: #350059;
  background-color: #cd91f9;
  transform: translateY(1px);
}
</style>
